// frontend/src/pages/Registrati.tsx
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { authApi } from '../services/api'

const DOMINI_AMMESSI = ['@imeco.me', '@deltaict.it']

// Le classi lp-* sono definite come <style> inline dentro Login.tsx, quindi
// esistono solo mentre quel componente è montato: essendo Registrati una
// pagina separata, il sottoinsieme di stile che usiamo va ripetuto qui
// (stessa tecnica, nessuna estrazione in un file condiviso per non toccare
// Login.tsx — pattern già così nel resto del progetto).
function StileRegistrazione() {
  return (
    <style>{`
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      html, body { min-height: 100%; }
      body {
        font-family: 'DM Sans', sans-serif;
        background: linear-gradient(135deg, #f8fafc, #eef2ff, #faf5ff);
      }
      .lp-page {
        min-height: 100vh; display: flex; flex-direction: column;
        align-items: center; justify-content: center; padding: 32px 16px 64px;
      }
      .lp-card {
        width: 100%; max-width: 400px;
        background: #ffffff; border-radius: 24px; padding: 40px 36px;
        box-shadow: 0 20px 60px rgba(0,0,0,.10), 0 0 0 1px rgba(0,0,0,.05);
      }
      .lp-card-title {
        font-family: 'Syne', sans-serif; font-size: 22px; font-weight: 800;
        color: #0f172a; letter-spacing: -.02em; margin-bottom: 6px;
      }
      .lp-card-sub { font-size: 14px; color: #94a3b8; margin-bottom: 26px; }
      .lp-label {
        display: block; font-size: 11px; font-weight: 600; color: #475569;
        letter-spacing: .07em; text-transform: uppercase; margin-bottom: 7px;
      }
      .lp-input {
        display: block; width: 100%; padding: 14px 16px; font-size: 16px;
        font-family: 'DM Sans', sans-serif; color: #0f172a; background: #f8fafc;
        border: 1.5px solid #e2e8f0; border-radius: 12px; outline: none;
      }
      .lp-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); background: #fff; }
      .lp-field { margin-bottom: 16px; }
      .lp-btn {
        display: block; width: 100%; padding: 15px; min-height: 52px; border: none;
        border-radius: 14px; background: linear-gradient(90deg, #2563eb, #7c3aed);
        color: #fff; font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 700;
        cursor: pointer; margin-top: 6px;
      }
      .lp-btn:disabled { background: #94a3b8; cursor: not-allowed; }
      .lp-forgot {
        display: block; width: 100%; background: none; border: none; color: #2563eb;
        font-size: 14px; font-family: 'DM Sans', sans-serif; cursor: pointer;
        padding: 10px 0; min-height: 44px; text-align: center; margin-top: 4px;
      }
      .lp-error {
        background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px;
        padding: 11px 14px; font-size: 13px; color: #b91c1c; margin-bottom: 12px;
      }
    `}</style>
  )
}

export default function Registrati() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [nome, setNome] = useState('')
  const [cognome, setCognome] = useState('')
  const [telefonoWhatsapp, setTelefonoWhatsapp] = useState('')
  const [loading, setLoading] = useState(false)
  const [inviata, setInviata] = useState(false)
  const [errore, setErrore] = useState('')

  const dominioValido = DOMINI_AMMESSI.some(d => email.toLowerCase().endsWith(d))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrore('')
    if (!dominioValido) {
      setErrore('Email non ammessa per la registrazione')
      return
    }
    setLoading(true)
    try {
      await authApi.registrazioni.crea({
        email, nome, cognome,
        telefono_whatsapp: telefonoWhatsapp || undefined,
      })
      setInviata(true)
    } catch (err: any) {
      setErrore(err?.response?.data?.detail || 'Errore durante l\'invio della richiesta')
    } finally {
      setLoading(false)
    }
  }

  if (inviata) {
    return (
      <div className="lp-page">
        <StileRegistrazione />
        <div className="lp-card">
          <div className="lp-card-title">Richiesta inviata</div>
          <p className="lp-card-sub">
            Riceverai un'email con le credenziali di accesso non appena la richiesta sarà approvata.
          </p>
          <button type="button" className="lp-btn" onClick={() => navigate('/login')}>
            Torna al login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="lp-page">
      <StileRegistrazione />
      <div className="lp-card">
        <div className="lp-card-title">Richiedi un account XSophia</div>
        <form onSubmit={handleSubmit}>
          <div className="lp-field">
            <label htmlFor="reg-email" className="lp-label">Email</label>
            <input
              id="reg-email" className="lp-input" type="email"
              placeholder="nome@imeco.me" value={email}
              onChange={e => setEmail(e.target.value)} required
            />
          </div>
          <div className="lp-field">
            <label htmlFor="reg-nome" className="lp-label">Nome</label>
            <input id="reg-nome" className="lp-input" value={nome} onChange={e => setNome(e.target.value)} required />
          </div>
          <div className="lp-field">
            <label htmlFor="reg-cognome" className="lp-label">Cognome</label>
            <input id="reg-cognome" className="lp-input" value={cognome} onChange={e => setCognome(e.target.value)} required />
          </div>
          <div className="lp-field">
            <label htmlFor="reg-telefono" className="lp-label">Telefono WhatsApp (opzionale)</label>
            <input id="reg-telefono" className="lp-input" value={telefonoWhatsapp} onChange={e => setTelefonoWhatsapp(e.target.value)} />
          </div>

          {errore && <div className="lp-error">{errore}</div>}

          <button type="submit" className="lp-btn" disabled={loading}>
            {loading ? 'Invio in corso...' : 'Invia richiesta'}
          </button>
          <button type="button" className="lp-forgot" onClick={() => navigate('/login')}>
            Torna al login
          </button>
        </form>
      </div>
    </div>
  )
}
